/**
 * Pure presentation for the inline-edit mode of a line-item row.
 *
 * Extracted from {@link LineItemRowView} so the parent stays under the
 * project's cognitive-complexity threshold while still satisfying the
 * `single-component-per-file` rule.
 * @module features/contracts/components/LineItemInlineEditRow/LineItemInlineEditRowView
 */

import { Check, X } from "lucide-react-native";
import React, { memo } from "react";

import { Button } from "@/components/atoms/Button";
import { Stack } from "@/components/atoms/Stack";
import { Input } from "@/components/atoms/Input";

/** Props for {@link LineItemInlineEditRowView}. */
export interface LineItemInlineEditRowViewProps {
  /** Category key for testID namespacing. */
  readonly category: string;
  /** Item index for testID namespacing. */
  readonly index: number;
  /** Working draft text held while the user edits. */
  readonly draftText: string;
  /** Whether the edit mutation is in flight. */
  readonly isEditingMutation: boolean;
  /** Invoked when the input value changes. */
  readonly onChangeEditDraft: (text: string) => void;
  /** Invoked when the user confirms the edit. */
  readonly onSaveEdit: () => void;
  /** Invoked when the user cancels the edit. */
  readonly onCancelEdit: () => void;
}

/**
 * Renders the inline edit input with save + cancel buttons.
 * @param props - Component props
 * @param props.category - Category key for testID namespacing.
 * @param props.index - Item index for testID namespacing.
 * @param props.draftText - Working draft text held while the user edits.
 * @param props.isEditingMutation - Whether the edit mutation is in flight.
 * @param props.onChangeEditDraft - Invoked when the input value changes.
 * @param props.onSaveEdit - Invoked when the user confirms the edit.
 * @param props.onCancelEdit - Invoked when the user cancels the edit.
 * @returns The inline-edit row
 */
const LineItemInlineEditRowView = ({
  category,
  index,
  draftText,
  isEditingMutation,
  onChangeEditDraft,
  onSaveEdit,
  onCancelEdit,
}: LineItemInlineEditRowViewProps) => (
  <Stack
    direction="horizontal"
    className="items-center gap-2 rounded-none bg-surface-info-subtle p-2"
  >
    <Input
      size="sm"
      variant="outline"
      isDisabled={isEditingMutation}
      className="flex-1"
      field={{
        testID: `line-item-edit-input-${category}-${index}`,
        placeholder: "Edit line item",
        value: draftText,
        onChangeText: onChangeEditDraft,
        editable: !isEditingMutation,
      }}
    />
    <Button
      testID={`line-item-save-${category}-${index}`}
      accessibilityLabel="Save edit"
      size="sm"
      variant="solid"
      tone="primary"
      icon={Check}
      onPress={onSaveEdit}
      isDisabled={isEditingMutation || draftText.trim() === ""}
      isLoading={isEditingMutation}
    />
    <Button
      testID={`line-item-cancel-edit-${category}-${index}`}
      accessibilityLabel="Cancel edit"
      size="sm"
      variant="outline"
      tone="secondary"
      icon={X}
      onPress={onCancelEdit}
      isDisabled={isEditingMutation}
    />
  </Stack>
);

LineItemInlineEditRowView.displayName = "LineItemInlineEditRowView";

export default memo(LineItemInlineEditRowView);
